import { toast } from "@/components/ui/use-toast";
import {
  useLazyDownloadShippingQuery,
  type RebuiltDownload,
} from "@/services/conciliation-dashboard";
import { downloadFromBlob } from "@/utils/download-blob";
import { extractRtkErrorMessage } from "@/utils/extract-rtk-error";
import { useCallback } from "react";
import { DASHBOARD_LABELS, DASHBOARD_TOASTS } from "../messages";

export interface ShippingDownloadActions {
  download: (shippingId: number, kindLabel: string) => void;
  isDownloading: boolean;
}

const isDownload = (result: unknown): result is RebuiltDownload =>
  !!result && typeof result === "object" && "blob" in result;

export const useShippingDownload = (): ShippingDownloadActions => {
  const [trigger, state] = useLazyDownloadShippingQuery();

  const download = useCallback(
    (shippingId: number, kindLabel: string): void => {
      toast(DASHBOARD_TOASTS.DOWNLOAD_REQUESTED(kindLabel));

      void (async (): Promise<void> => {
        try {
          const result: unknown = await trigger(shippingId).unwrap();

          if (!isDownload(result)) {
            toast(DASHBOARD_TOASTS.DOWNLOAD_FAILED(kindLabel));
            return;
          }

          downloadFromBlob(result.blob, result.filename);

          if (!result.matchesSent) {
            toast(DASHBOARD_TOASTS.DOWNLOAD_DIVERGED(kindLabel));
          }
        } catch (error: unknown) {
          toast(
            DASHBOARD_TOASTS.DOWNLOAD_FAILED(
              extractRtkErrorMessage(error, DASHBOARD_LABELS.DOWNLOAD_FALLBACK),
            ),
          );
        }
      })();
    },
    [trigger],
  );

  return { download, isDownloading: state.isLoading };
};
